import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../components/BackButton";
import { API_URL } from "../config";

function VerifyAccount() {
    const navigate = useNavigate();

    // ✅ 1. FAST LOAD LOGIC: LocalStorage se data uthao
    const savedData = localStorage.getItem("user");
    const parsedUser = savedData ? JSON.parse(savedData) : null;

    const [user, setUser] = useState(parsedUser);

    // ✅ 2. LOADING LOGIC
    const [loading, setLoading] = useState(!parsedUser);

    // 🔥 NEW: Uploading status track karne ke liye
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Modal State
    const [activeModal, setActiveModal] = useState(null); // 'email', 'pan', 'bank', 'upi'
    const [alertModal, setAlertModal] = useState({ show: false, message: "", type: "success" }); // type can be 'success' or 'error'
    const [step, setStep] = useState(1);
    const [timer, setTimer] = useState(0);

    // Combined Form Data State
    const [formData, setFormData] = useState({
        otp: "",
        panName: "",
        panNumber: "",
        panImage: null,
        accountNumber: "",
        confirmAccountNumber: "",
        ifsc: "",
        upiId: ""
    });

    const token = localStorage.getItem("token");

    // 1. FETCH PROFILE (Background)
    const fetchProfile = async () => {
        try {
            const res = await fetch(`${API_URL}/api/user/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();

            if (data.data) {
                setUser(data.data);
                localStorage.setItem("user", JSON.stringify(data.data));
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) navigate("/auth");
        else fetchProfile();
        // eslint-disable-next-line
    }, []);

    // Timer Logic
    useEffect(() => {
        let interval;
        if (timer > 0) interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    // Open Modal & Reset Data
    const handleOpenModal = (type) => {
        // 🔥 Agar pehle se pending ya verified hai toh dobara modal open mat karo
        if (type === 'pan' && ['PENDING', 'VERIFIED'].includes(user?.kyc?.pan?.status)) return;
        if (type === 'bank' && ['PENDING', 'VERIFIED'].includes(user?.kyc?.bank?.status)) return;
        if (type === 'upi' && ['PENDING', 'VERIFIED'].includes(user?.kyc?.upi?.status)) return;

        setActiveModal(type);
        setStep(1);
        setFormData({
            otp: "",
            panName: "",
            panNumber: "",
            panImage: null,
            accountNumber: "",
            confirmAccountNumber: "",
            ifsc: "",
            upiId: ""
        });
    };

    // --- HANDLERS ---

    // 📩 EMAIL OTP SEND LOGIC
    const handleSendOTP = async () => {
        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/auth/send-email-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ email: user.email })
            });
            const data = await res.json();

            if (data.success) {
                setStep(2);
                setTimer(60);
            } else {
                alert(data.message || "Failed to send OTP");
            }
        } catch (error) {
            alert("Network error, please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ✅ EMAIL OTP VERIFY LOGIC
    const handleVerifyOTP = async () => {
        if (!formData.otp) {
            return setAlertModal({ show: true, message: "Please enter OTP", type: "error" });
        }

        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/auth/verify-email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ email: user.email, otp: formData.otp })
            });
            const data = await res.json();

            if (data.success) {
                setAlertModal({ show: true, message: "Email Verified Successfully!", type: "success" });
                setActiveModal(null);
                fetchProfile();
            } else {
                setAlertModal({ show: true, message: data.message || "Invalid or Expired OTP", type: "error" });
            }
        } catch (error) {
            setAlertModal({ show: true, message: "Network error, please try again.", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 🟢 PAN SUBMIT LOGIC
    const handlePanSubmit = async () => {
        if (!user.name || !formData.panNumber || !formData.panImage) {
            return setAlertModal({ show: true, message: "Please fill all PAN details & upload an image!", type: "error" });
        }

        setIsSubmitting(true);
        try {
            const submitData = new FormData();
            submitData.append("fullName", user.name);
            submitData.append("panNumber", formData.panNumber);
            submitData.append("panImage", formData.panImage);

            const res = await fetch(`${API_URL}/api/kyc/submit-pan`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: submitData
            });

            const data = await res.json();
            if (data.success) {
                setAlertModal({ show: true, message: "PAN Details Submitted! Verification usually takes 24-48 hours", type: "success" });
                setActiveModal(null);
                fetchProfile();
            } else {
                setAlertModal({ show: true, message: data.message || "Submission failed", type: "error" });
            }
        } catch (error) {
            setAlertModal({ show: true, message: "Network error, please try again.", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 🔵 BANK SUBMIT LOGIC
    const handleBankSubmit = async () => {
        if (!formData.accountNumber || !formData.confirmAccountNumber || !formData.ifsc) {
            return setAlertModal({ show: true, message: "Please fill all bank details", type: "error" });
        }
        if (formData.accountNumber !== formData.confirmAccountNumber) {
            return setAlertModal({ show: true, message: "Account numbers do not match!", type: "error" });
        }

        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/kyc/submit-bank`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    accountName: user.name,
                    accountNumber: formData.accountNumber,
                    ifsc: formData.ifsc
                })
            });

            const data = await res.json();
            if (data.success) {
                setAlertModal({ show: true, message: "Bank Details Submitted! Verification usually takes 24-48 hours", type: "success" });
                setActiveModal(null);
                fetchProfile();
            } else {
                setAlertModal({ show: true, message: data.message || "Submission failed", type: "error" });
            }
        } catch (error) {
            setAlertModal({ show: true, message: "Network error, please try again.", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 🟠 UPI SUBMIT LOGIC
    const handleUpiSubmit = async () => {
        if (!formData.upiId) return setAlertModal({ show: true, message: "Please enter your UPI ID", type: "error" });
        if (!formData.upiId.includes("@")) return setAlertModal({ show: true, message: "Invalid UPI ID format (Must contain @)", type: "error" });

        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/kyc/submit-upi`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ upiId: formData.upiId })
            });

            const data = await res.json();
            if (data.success) {
                setAlertModal({ show: true, message: "UPI ID Submitted! Verification usually takes 24-48 hours", type: "success" });
                setActiveModal(null);
                fetchProfile();
            } else {
                setAlertModal({ show: true, message: data.message || "Submission failed", type: "error" });
            }
        } catch (error) {
            setAlertModal({ show: true, message: "Network error, please try again.", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ✅ SHIMMER LOADER (MAROON THEME)
    if (loading) return (
        <div style={{ background: "#f8f9fa", minHeight: "100vh" }}>
            <div className="placeholder-glow" style={{
                height: "240px",
                background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)",
                borderBottomLeftRadius: "30px",
                borderBottomRightRadius: "30px"
            }}></div>
            <div className="container mt-5">
                <div className="d-flex flex-column gap-3 placeholder-glow">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="card border-0 shadow-lg rounded-4 p-4" style={{ height: "90px" }}>
                            <div className="d-flex align-items-center">
                                <span className="placeholder rounded-circle bg-secondary opacity-25 me-3" style={{ width: "50px", height: "50px" }}></span>
                                <div className="flex-grow-1">
                                    <span className="placeholder col-6 bg-secondary opacity-25 rounded mb-1"></span>
                                    <span className="placeholder col-4 bg-secondary opacity-25 rounded d-block"></span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // 🔥 STATUS DISPLAY LOGIC
    const getPanStatusDisplay = () => {
        const status = user?.kyc?.pan?.status;
        if (status === 'VERIFIED') return user.kyc.pan.number;
        if (status === 'PENDING') return <span className="text-warning fw-bold"><i className="bi bi-clock-history"></i> Pending Approval</span>;
        if (status === 'REJECTED') return <span className="text-danger fw-bold"><i className="bi bi-x-circle"></i> Rejected</span>;
        return "Not Linked";
    };

    const getBankStatusDisplay = () => {
        const status = user?.kyc?.bank?.status;
        if (status === 'VERIFIED') return `XXXX${user.kyc.bank.accountNumber.slice(-4)}`;
        if (status === 'PENDING') return <span className="text-warning fw-bold"><i className="bi bi-clock-history"></i> Pending Approval</span>;
        if (status === 'REJECTED') return <span className="text-danger fw-bold"><i className="bi bi-x-circle"></i> Rejected</span>;
        return "Not Linked";
    };

    const getUpiStatusDisplay = () => {
        const status = user?.kyc?.upi?.status;
        if (status === 'VERIFIED') return user.kyc.upi.upiId;
        if (status === 'PENDING') return <span className="text-warning fw-bold"><i className="bi bi-clock-history"></i> Pending Approval</span>;
        if (status === 'REJECTED') return <span className="text-danger fw-bold"><i className="bi bi-x-circle"></i> Rejected</span>;
        return "Not Linked";
    };

    // 🔥 FIX: PROGRESS BAR LOGIC (Sirf in 4 ka 25% each = 100%)
    const isEmailDone = Boolean(user?.isEmailVerified);
    const isPanDone = user?.kyc?.pan?.status === 'VERIFIED';
    const isBankDone = user?.kyc?.bank?.status === 'VERIFIED';
    const isUpiDone = user?.kyc?.upi?.status === 'VERIFIED';

    let progress = 0;
    if (isEmailDone) progress += 25;
    if (isPanDone) progress += 25;
    if (isBankDone) progress += 25;
    if (isUpiDone) progress += 25;

    const isFullyVerified = progress === 100;

    // Verification List Data
    const verificationItems = [
        {
            key: "email",
            title: "Email Address",
            value: user?.email,
            isVerified: isEmailDone,
            icon: "envelope-fill",
            color: "#ffc107",
            desc: "For important updates & OTPs"
        },
        {
            key: "pan",
            title: "PAN Card",
            value: getPanStatusDisplay(),
            isVerified: isPanDone,
            icon: "person-vcard-fill",
            color: "#198754",
            desc: "Mandatory for withdrawals"
        },
        {
            key: "bank",
            title: "Bank Account",
            value: getBankStatusDisplay(),
            isVerified: user?.kyc?.bank?.status === 'VERIFIED',
            icon: "bank2",
            color: "#6610f2",
            desc: "To receive your winnings"
        },
        {
            key: "upi",
            title: "UPI ID",
            value: getUpiStatusDisplay(),
            isVerified: user?.kyc?.upi?.status === 'VERIFIED',
            icon: "qr-code",
            color: "#fd7e14",
            desc: "For instant transfers"
        }
    ];

    return (
        <div>
            {/* === HERO HEADER IN MAROON THEME === */}
            <div
                className="pt-5 pb-5 px-3 position-relative overflow-hidden"
                style={{
                    background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)",
                    borderBottomLeftRadius: "30px",
                    borderBottomRightRadius: "30px",
                    height: "auto", // Height auto kar di progress bar fit karne ke liye
                    minHeight: "260px"
                }}
            >
                <div className="position-relative z-1 container-fluid" style={{ marginTop: "-30px" }}>
                    <BackButton className="text-white bg-transparent border-0" />
                </div>

                <div className="container text-center" style={{ marginTop: "-20px" }}>
                    <div className="position-relative d-inline-block">
                        <img
                            src={user?.avatar || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                            alt="User"
                            className="rounded-circle border border-4 border-white shadow-lg bg-white"
                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />

                        {/* 🔥 NEW: BLUE TICK IF 100% VERIFIED */}
                        {isFullyVerified && (
                            <i
                                className="bi bi-patch-check-fill position-absolute animate-bounce"
                                style={{
                                    bottom: "2px", right: "2px",
                                    color: "#1d9bf0", // Verified Blue
                                    fontSize: "26px",
                                    background: "white",
                                    borderRadius: "50%",
                                    lineHeight: "1",
                                    padding: "0px",
                                    border: "2px solid white"
                                }}
                                title="Fully Verified User"
                            ></i>
                        )}
                    </div>
                    <h5 className="fw-bold mt-2 mb-0 text-white">{user?.name || "User"}</h5>
                </div>

                {/* 🔥 NEW: DYNAMIC PROGRESS BAR */}
                <div className="container mt-4 mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1 px-1">
                        <span className="text-white small fw-bold">Verification Status</span>
                        <span className="text-white small fw-bold">{progress}%</span>
                    </div>
                    <div className="progress shadow-sm" style={{ height: "10px", backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "10px" }}>
                        <div
                            className={`progress-bar progress-bar-striped progress-bar-animated ${progress === 100 ? 'bg-success' : 'bg-warning'}`}
                            role="progressbar"
                            style={{ width: `${progress}%`, transition: "width 1s ease-in-out" }}
                            aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"
                        ></div>
                    </div>
                    <div className="text-center mt-2">
                        {isFullyVerified ? (
                            <p className="text-success fw-bold small mb-0 bg-white rounded-pill d-inline-block px-3 py-1 shadow-sm">
                                <i className="bi bi-check-circle-fill me-1"></i> 🎉 Account Fully Verified! Withdrawals Unlocked.
                            </p>
                        ) : (
                            <p className="text-white opacity-75 small mb-0">
                                Complete remaining steps to unlock withdrawals
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="container mt-4">
                <div className="d-flex flex-column gap-3">
                    {verificationItems.map((item) => {
                        // Check if current item is PENDING
                        const isPending =
                            (item.key === 'pan' && user?.kyc?.pan?.status === 'PENDING') ||
                            (item.key === 'bank' && user?.kyc?.bank?.status === 'PENDING') ||
                            (item.key === 'upi' && user?.kyc?.upi?.status === 'PENDING');

                        return (
                            <div key={item.key} className="card border-0 shadow-lg rounded-4 overflow-hidden position-relative">
                                <div className="position-absolute top-0 bottom-0 start-0" style={{ width: "5px", background: item.color }}></div>

                                <div className="card-body p-4 d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center text-white me-3 shadow-sm"
                                            style={{ width: "50px", height: "50px", background: item.color }}
                                        >
                                            <i className={`bi bi-${item.icon} fs-5`}></i>
                                        </div>

                                        <div>
                                            <h6 className="fw-bold text-dark mb-0">{item.title}</h6>
                                            <small className="text-muted d-block mb-1" style={{ fontSize: "11px" }}>{item.desc}</small>

                                            {item.isVerified ? (
                                                <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill">
                                                    Verified <i className="bi bi-check-circle-fill ms-1"></i>
                                                </span>
                                            ) : (
                                                <span className="text-dark fw-bold small">
                                                    {item.value}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Hide action button if verified OR pending */}
                                    {!item.isVerified && !isPending && (
                                        <button
                                            className="btn btn-outline-dark rounded-circle p-0 d-flex align-items-center justify-content-center shadow-sm"
                                            style={{ width: "40px", height: "40px" }}
                                            onClick={() => handleOpenModal(item.key)}
                                        >
                                            <i className="bi bi-chevron-right"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className="text-center mt-4 mb-4 text-muted opacity-75">
                    <i className="bi bi-shield-lock-fill fs-2 d-block mb-2" style={{ color: "#800000" }}></i>
                    <small className="fw-bold text-uppercase ls-1" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>100% Secure & Encrypted</small>
                </div>
            </div>

            {/* =================================================
          🔥 MODALS
      ================================================= */}
            {activeModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(8px)", backgroundColor: "rgba(0,0,0,0.6)" }}></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">

                                <div className="modal-header border-0 text-white p-4" style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)" }}>
                                    <div>
                                        <h5 className="modal-title fw-bold mb-1">
                                            {activeModal === 'bank' ? "Link Bank Account" :
                                                activeModal === 'pan' ? "Verify PAN Card" :
                                                    activeModal === 'upi' ? "Link UPI ID" :
                                                        "Verify Email"}
                                        </h5>
                                        <p className="mb-0 small opacity-75">Please provide accurate details.</p>
                                    </div>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setActiveModal(null)}></button>
                                </div>

                                <div className="modal-body p-4 bg-light">

                                    {/* EMAIL MODAL */}
                                    {activeModal === 'email' && (
                                        step === 1 ? (
                                            <div className="text-center py-2">
                                                <div className="p-3 bg-white rounded-circle shadow-sm d-inline-block mb-3">
                                                    <i className="bi bi-envelope-open-fill text-warning fs-1"></i>
                                                </div>
                                                <h6 className="fw-bold text-dark">{user.email}</h6>
                                                <p className="text-muted small mb-4">We will send a 6-digit OTP to this email.</p>
                                                <button
                                                    className="btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm"
                                                    style={{ background: "linear-gradient(to right, #f2994a, #f2c94c)", border: "none", color: "#000" }}
                                                    onClick={handleSendOTP}
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? "Sending OTP..." : "Send OTP Now"}
                                                </button>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="form-floating mb-3">
                                                    <input type="number" className="form-control rounded-3 border-0 shadow-sm text-center fw-bold fs-4" id="otpInput" placeholder="000000" value={formData.otp} onChange={(e) => setFormData({ ...formData, otp: e.target.value })} style={{ letterSpacing: "5px" }} />
                                                    <label htmlFor="otpInput" className="text-center w-100">Enter OTP</label>
                                                </div>
                                                <button
                                                    className="btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm text-white"
                                                    style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", border: "none" }}
                                                    onClick={handleVerifyOTP}
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? "Verifying..." : "Verify Email"}
                                                </button>
                                                <div className="text-center mt-3">
                                                    {timer > 0 ? (
                                                        <small className="text-muted fw-bold">Resend in {timer}s</small>
                                                    ) : (
                                                        <button className="btn btn-link btn-sm text-decoration-none" onClick={handleSendOTP} disabled={isSubmitting}>
                                                            Resend OTP
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {/* PAN MODAL */}
                                    {activeModal === 'pan' && (
                                        <div>
                                            {user?.kyc?.pan?.status === 'REJECTED' && (
                                                <div className="alert alert-danger py-2 small border-0 shadow-sm">
                                                    <i className="bi bi-exclamation-octagon-fill me-2"></i>
                                                    <strong>Rejected:</strong> {user?.kyc?.pan?.rejectionReason || "Invalid Document provided."}
                                                </div>
                                            )}

                                            <div className="alert alert-warning py-2 small border-0 mb-3 shadow-sm rounded-3">
                                                <i className="bi bi-exclamation-circle-fill me-1"></i> Name on PAN must match: <strong>{user.name}</strong>
                                            </div>

                                            <div className="form-floating mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control rounded-3 border-0 shadow-sm fw-bold text-secondary"
                                                    id="panName"
                                                    value={user.name}
                                                    readOnly
                                                    style={{ backgroundColor: "#e9ecef" }}
                                                />
                                                <label htmlFor="panName" className="text-muted">Full Name (As on PAN)</label>
                                            </div>

                                            <div className="form-floating mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control rounded-3 border-0 shadow-sm text-uppercase fw-bold"
                                                    id="panNum"
                                                    placeholder="Num"
                                                    maxLength={10}
                                                    value={formData.panNumber}
                                                    onChange={(e) => setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })}
                                                />
                                                <label htmlFor="panNum" className="text-muted">PAN Number</label>
                                            </div>

                                            <div className="mb-4">
                                                <label className="form-label small fw-bold text-muted ps-1">Upload PAN Photo</label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="form-control rounded-3 border-0 shadow-sm"
                                                    onChange={(e) => setFormData({ ...formData, panImage: e.target.files[0] })}
                                                />
                                            </div>

                                            <button
                                                className="btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm d-flex justify-content-center align-items-center gap-2 text-white"
                                                style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", border: "none" }}
                                                onClick={handlePanSubmit}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Uploading Securely...</>
                                                ) : "Submit Verification"}
                                            </button>
                                        </div>
                                    )}

                                    {/* BANK MODAL */}
                                    {activeModal === 'bank' && (
                                        <div>
                                            {user?.kyc?.bank?.status === 'REJECTED' && (
                                                <div className="alert alert-danger py-2 small border-0 shadow-sm">
                                                    <i className="bi bi-exclamation-octagon-fill me-2"></i>
                                                    <strong>Rejected:</strong> {user?.kyc?.bank?.rejectionReason || "Details mismatch."}
                                                </div>
                                            )}
                                            <div className="alert alert-warning py-2 small border-0 mb-3 shadow-sm rounded-3"><i className="bi bi-exclamation-circle-fill me-1"></i> Name must match: <strong>{user.name}</strong></div>
                                            <div className="form-floating mb-3">
                                                <input type="text" className="form-control rounded-3 border-0 shadow-sm fw-bold text-secondary" value={user.name} readOnly style={{ backgroundColor: "#e9ecef" }} />
                                                <label>Account Holder Name</label>
                                            </div>
                                            <div className="form-floating mb-3">
                                                <input type="number" className="form-control rounded-3 border-0 shadow-sm" id="accNum" placeholder="Account" value={formData.accountNumber} onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })} />
                                                <label htmlFor="accNum" className="text-muted">Account Number</label>
                                            </div>
                                            <div className="form-floating mb-3">
                                                <input type="number" className="form-control rounded-3 border-0 shadow-sm" id="confNum" placeholder="Confirm" value={formData.confirmAccountNumber} onChange={(e) => setFormData({ ...formData, confirmAccountNumber: e.target.value })} />
                                                <label htmlFor="confNum" className="text-muted">Re-enter Account Number</label>
                                            </div>
                                            <div className="form-floating mb-4">
                                                <input type="text" className="form-control rounded-3 border-0 shadow-sm text-uppercase" id="ifsc" placeholder="IFSC" maxLength={11} value={formData.ifsc} onChange={(e) => setFormData({ ...formData, ifsc: e.target.value.toUpperCase() })} />
                                                <label htmlFor="ifsc" className="text-muted">IFSC Code</label>
                                            </div>
                                            <button
                                                className="btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm d-flex justify-content-center align-items-center gap-2 text-white"
                                                style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", border: "none" }}
                                                onClick={handleBankSubmit}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Submitting...</>
                                                ) : "Link Bank Account"}
                                            </button>
                                        </div>
                                    )}

                                    {/* UPI MODAL */}
                                    {activeModal === 'upi' && (
                                        <div>
                                            {user?.kyc?.upi?.status === 'REJECTED' && (
                                                <div className="alert alert-danger py-2 small border-0 shadow-sm">
                                                    <i className="bi bi-exclamation-octagon-fill me-2"></i>
                                                    <strong>Rejected:</strong> {user?.kyc?.upi?.rejectionReason || "Invalid UPI ID."}
                                                </div>
                                            )}
                                            <div className="alert alert-warning py-2 small border-0 mb-3 shadow-sm rounded-3"><i className="bi bi-exclamation-circle-fill me-1"></i> Name on UPI must match: <strong>{user.name}</strong></div>
                                            <div className="form-floating mb-3">
                                                <input type="text" className="form-control rounded-3 border-0 shadow-sm fw-bold text-secondary" value={user.name} readOnly style={{ backgroundColor: "#e9ecef" }} />
                                                <label>Account Holder Name</label>
                                            </div>
                                            <div className="form-floating mb-4">
                                                <input type="text" className="form-control rounded-3 border-0 shadow-sm" id="upiId" placeholder="UPI" value={formData.upiId} onChange={e => setFormData({ ...formData, upiId: e.target.value })} />
                                                <label htmlFor="upiId" className="text-muted">Enter UPI ID (e.g. mobile@ybl)</label>
                                            </div>
                                            <button
                                                className="btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm d-flex justify-content-center align-items-center gap-2 text-white"
                                                style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", border: "none" }}
                                                onClick={handleUpiSubmit}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Verifying...</>
                                                ) : "Verify & Link UPI"}
                                            </button>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* 🔥 PREMIUM ALERT MODAL 🔥 */}
            {alertModal.show && (
                <>
                    <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1080 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1090 }}>
                        <div className="modal-dialog modal-dialog-centered px-4">
                            <div className="modal-content border-0 shadow-lg rounded-4 p-4 text-center animate-bounce">

                                {/* Dynamic Icon based on success/error */}
                                <div className={`mb-3 text-${alertModal.type === 'error' ? 'danger' : 'success'}`}>
                                    <i className={`bi bi-${alertModal.type === 'error' ? 'x-circle-fill' : 'check-circle-fill'} display-1`}></i>
                                </div>

                                <h4 className="fw-bold mb-2 text-dark">
                                    {alertModal.type === 'error' ? 'Oops!' : 'Success!'}
                                </h4>

                                <p className="text-muted mb-4 px-2" style={{ fontSize: "15px" }}>
                                    {alertModal.message}
                                </p>

                                <button
                                    className={`btn w-100 rounded-pill py-3 fw-bold shadow-sm text-white`}
                                    style={{ background: alertModal.type === 'error' ? '#dc3545' : '#198754' }}
                                    onClick={() => setAlertModal({ show: false, message: "", type: "success" })}
                                >
                                    Okay
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Animation CSS */}
            <style>{`
                .animate-bounce { animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) both; }
                @keyframes bounceIn {
                    0% { transform: scale(0.5); opacity: 0; }
                    80% { transform: scale(1.05); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
}

export default VerifyAccount;